import React, {
   memo,
   ReactNode,
   useCallback,
   useRef,
   useEffect,
} from 'react';
import { Virtuoso, VirtuosoHandle, StateSnapshot } from 'react-virtuoso';
import { ScrollableAncestorContext } from 'contexts';

type VirtuosoListProps<T> = {
   virtuosoRef?: React.RefObject<VirtuosoHandle>;
   data: T[];
   hasNext: boolean;
   onReachEnd: () => void;
   isFetchingNext: boolean;
   renderItem: (index: number, item: T) => ReactNode;
   renderHeader?: () => ReactNode;
   renderLoading: () => ReactNode;
   className?: string;
   restoreStateFrom?: StateSnapshot;
   customScrollParent?: HTMLElement | null;
};

const VirtuosoList = <T,>({
   data,
   onReachEnd,
   isFetchingNext,
   hasNext,
   renderItem,
   renderHeader,
   renderLoading,
   className = 'h-full',
   restoreStateFrom,
   virtuosoRef,
   customScrollParent,
}: VirtuosoListProps<T>) => {
   const [scrollerElement, setScrollerElement] = React.useState<HTMLElement | null>(null);
   const containerRef = useRef<HTMLDivElement>(null);

   const handleEndReached = useCallback(() => {
      if (hasNext && !isFetchingNext) {
         onReachEnd();
      }
   }, [hasNext, isFetchingNext, onReachEnd]);

   const Header = useCallback(() => {
      if (renderHeader) {
         return <>{renderHeader()}</>;
      }
      return null;
   }, [renderHeader]);

   const Footer = useCallback(() => {
      if (hasNext) {
         return <div>{renderLoading()}</div>;
      }
      return null;
   }, [hasNext, renderLoading]);

   useEffect(() => {
      const scroller = customScrollParent ?? containerRef.current?.firstElementChild as HTMLElement;
      if (scroller) setScrollerElement(scroller);
   }, [customScrollParent]);

   const scrollableAncestor = customScrollParent ?? scrollerElement;

   return (
      <ScrollableAncestorContext.Provider value={scrollableAncestor}>
         <div ref={containerRef} className={className}>
            <Virtuoso
               ref={virtuosoRef}
               className={className}
               style={{ height: '100%' }}
               data={data}
               itemContent={renderItem}
               endReached={handleEndReached}
               components={{
                  Header,
                  Footer,
               }}
               overscan={20}
               restoreStateFrom={restoreStateFrom ?? undefined}
               customScrollParent={customScrollParent ?? undefined}
            />
         </div>
      </ScrollableAncestorContext.Provider>
   );
};

export default memo(VirtuosoList) as typeof VirtuosoList;
